import apiClient from "../common";

const JSON_API_MEDIA = "application/vnd.api+json";

export type SupplyDocumentLineItem = {
  product_code: string;
  description?: string;
  bulks: number;
  units_per_bulk?: number;
  total_units?: number;
  unit_fob?: number;
  line_fob_total?: number;
  /**
   * OJO: es el atributo de producto "Código Proveedor" del maestro, que en la
   * práctica trae referencias del proveedor ("#3013TL-card") o "0.0", NO el
   * `vendorCode` del ERP. El código que Profit necesita es
   * `SupplyDocumentRow.supplier_code`, a nivel de documento.
   */
  supplier_code?: string;
  unit_cost?: number | null;
  warehouse_code?: string;
  applies_tax?: boolean;
  tax_pctg?: number | null;
  discount_pctg?: number | null;
  uom?: string;
};

export type SupplyDocumentRow = {
  id: string;
  document_type: string;
  document_origin: string;
  document_number: string;
  document_status: string;
  expected_receipt_date: string | null;
  ataw_date: string | null;
  supplier_name: string;
  /** `vendorCode` del ERP (p. ej. "0176"). Ver la nota del campo homónimo. */
  supplier_code: string;
  line_items: SupplyDocumentLineItem[];
};

function readJsonApiAttr(
  attrs: Record<string, unknown>,
  snake: string,
  kebab: string,
): unknown {
  return attrs[snake] ?? attrs[kebab];
}

/** Número finito o `null`. Preserva el 0, que es un costo capturado válido. */
function numberOrNull(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseLineItems(raw: unknown): SupplyDocumentLineItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    const bulks = Number(row.bulks ?? 0);
    const unitsPerBulkRaw = row.units_per_bulk ?? row["units-per-bulk"];
    const unitsPerBulk =
      unitsPerBulkRaw != null ? Number(unitsPerBulkRaw) : undefined;
    const totalUnitsRaw = row.total_units ?? row["total-units"];
    const totalUnits =
      totalUnitsRaw != null ? Number(totalUnitsRaw) : undefined;
    const unitFobRaw = row.unit_fob ?? row["unit-fob"];
    const lineFobTotalRaw = row.line_fob_total ?? row["line-fob-total"];
    const unitFob =
      unitFobRaw != null && Number.isFinite(Number(unitFobRaw))
        ? Number(unitFobRaw)
        : undefined;
    const lineFobTotal =
      lineFobTotalRaw != null && Number.isFinite(Number(lineFobTotalRaw))
        ? Number(lineFobTotalRaw)
        : undefined;
    const supplierCode = String(
      row.supplier_code ?? row["supplier-code"] ?? "",
    ).trim();

    return {
      product_code: String(
        row.product_code ?? row["product-code"] ?? "",
      ).trim(),
      description: String(row.description ?? "").trim() || undefined,
      bulks: Number.isFinite(bulks) && bulks > 0 ? bulks : 1,
      units_per_bulk:
        unitsPerBulk != null && Number.isFinite(unitsPerBulk) && unitsPerBulk > 0
          ? unitsPerBulk
          : undefined,
      total_units:
        totalUnits != null && Number.isFinite(totalUnits) && totalUnits > 0
          ? totalUnits
          : undefined,
      unit_fob: unitFob,
      line_fob_total: lineFobTotal,
      supplier_code: supplierCode || undefined,
      unit_cost: numberOrNull(row.unit_cost ?? row["unit-cost"]),
      warehouse_code:
        String(row.warehouse_code ?? row["warehouse-code"] ?? "").trim() ||
        undefined,
      applies_tax:
        typeof (row.applies_tax ?? row["applies-tax"]) === "boolean"
          ? (row.applies_tax ?? row["applies-tax"]) as boolean
          : undefined,
      tax_pctg: numberOrNull(row.tax_pctg ?? row["tax-pctg"]),
      discount_pctg: numberOrNull(row.discount_pctg ?? row["discount-pctg"]),
      uom: String(row.uom ?? "").trim() || undefined,
    };
  });
}

function normalizeSupplyDocumentResource(
  row: Record<string, unknown>,
): SupplyDocumentRow | null {
  const attrs =
    (row.attributes as Record<string, unknown> | undefined) ?? row;
  const id = String(row.id ?? attrs.id ?? "").trim();
  if (!id) {
    return null;
  }
  return {
    id,
    document_type: String(
      readJsonApiAttr(attrs, "document_type", "document-type") ?? "",
    ),
    document_origin: String(
      readJsonApiAttr(attrs, "document_origin", "document-origin") ?? "",
    ),
    document_number: String(
      readJsonApiAttr(attrs, "document_number", "document-number") ?? "",
    ),
    document_status: String(
      readJsonApiAttr(attrs, "document_status", "document-status") ?? "",
    ),
    expected_receipt_date:
      (readJsonApiAttr(
        attrs,
        "expected_receipt_date",
        "expected-receipt-date",
      ) as string | null) ?? null,
    ataw_date:
      (readJsonApiAttr(attrs, "ataw_date", "ataw-date") as string | null) ??
      null,
    supplier_name: String(
      readJsonApiAttr(attrs, "supplier_name", "supplier-name") ?? "",
    ),
    supplier_code: String(
      readJsonApiAttr(attrs, "supplier_code", "supplier-code") ?? "",
    ).trim(),
    line_items: parseLineItems(
      readJsonApiAttr(attrs, "line_items", "line-items"),
    ),
  };
}

function extractSupplyDocumentResources(
  payload: unknown,
): Record<string, unknown>[] {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }
  if (typeof payload !== "object") {
    return [];
  }
  const body = payload as { data?: unknown };
  if (Array.isArray(body.data)) {
    return body.data as Record<string, unknown>[];
  }
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    const nested = body.data as { data?: unknown };
    if (Array.isArray(nested.data)) {
      return nested.data as Record<string, unknown>[];
    }
  }
  return [];
}

function parseSupplyDocumentListPayload(payload: unknown): SupplyDocumentRow[] {
  return extractSupplyDocumentResources(payload)
    .map(normalizeSupplyDocumentResource)
    .filter((row): row is SupplyDocumentRow => row != null);
}

/** Lista documentos simulados de una simulación, incluyendo líneas (SKUs). */
export async function apiListSupplyDocumentsForSimulation(
  simulationId: string,
): Promise<SupplyDocumentRow[]> {
  const res = await apiClient.get("supply-documents/", {
    params: {
      "filter[simulation_id]": simulationId,
      "page[size]": 100,
    },
    headers: { Accept: JSON_API_MEDIA },
  });
  const parsed = parseSupplyDocumentListPayload(res.data);
  return parsed.filter(
    (doc) =>
      doc.document_origin === "simulated" &&
      doc.document_type.toUpperCase() === "PO",
  );
}
